import type {ICustomerPortalService} from "../ICustomerPortalService";
import {Observable, of, throwError} from "rxjs";
import {inject, injectable, IServiceConfig, RestService} from "@athena-portal/core";
import {AjaxError, AjaxRequest, AjaxResponse} from "rxjs/ajax";
import {catchError, map} from "rxjs/operators";
import {Types} from "../../Types";
import {
    AnnualPolicyInfo,
    CoverChange,
    Document,
    FalconPaymentRequest,
    FalconPaymentResponse,
    FalconUser,
    FerryPaymentHistoryResponse,
    Mandate,
    MarketingPreference,
    MissedPayment,
    NewCardForm,
    Party,
    PartyProperty,
    Policy,
    PolicyProperty,
    Quote,
    Renewal,
    User,
    INotificationService,
    VerifyNewCardResponse,
    EnumPolicyFilter,
    CacheObservable,
    ClearCache,
    Types as JourneyTypes
} from "@ferry/journey-services";
import {IRequestModifier} from "@athena-portal/core/src/services/IRequestModifier";

/**
 * Rest implementation of the example service
 */
@injectable()
export class CustomerPortalServiceImpl extends RestService implements ICustomerPortalService {

    public configuration: IServiceConfig;
    private readonly notificationService: INotificationService;

    constructor(
        @inject(Types.CustomerPortalServiceConfig) configuration: IServiceConfig,
        @inject(JourneyTypes.NotificationService) notificationService: INotificationService
    ) {
        super();
        this.configuration = configuration;
        this.notificationService = notificationService
    }

    @CacheObservable(true)
    public getProfile(): Observable<Party> {
        return this.doRequest<Party>(`/profile`)
    }

    @CacheObservable(true)
    public getPolicies(filter?: EnumPolicyFilter, onlyCurrentPeriodPolicyVersions: boolean = false): Observable<Policy[]> {
        const searchParams = new URLSearchParams();

        if (filter) {
            searchParams.append("filter", EnumPolicyFilter[filter]);
        }

        searchParams.append("onlyCurrentPeriodPolicyVersions", onlyCurrentPeriodPolicyVersions.toString());

        return this.doRequest<Policy[]>(`/policies?${searchParams.toString()}`, "GET");
    }

    @CacheObservable(true)
    public getOldPolicies(): Observable<Policy[]> {
        return this.doRequest<Policy[]>(`/policies/old`, "GET");
    }

    @CacheObservable(true)
    public getPolicy(policyNumber: string): Observable<Policy> {
        return this.doRequest<Policy>(`/policies/${policyNumber}`)
    }

    @CacheObservable(true)
    public getDocumentsByPolicyNumber(policyNumber: string): Observable<Document[]> {
        return this.doRequest<Document[]>(`/policies/${policyNumber}/documents`);
    }

    @CacheObservable(true)
    public getAllDocumentsByPolicyNumber(policyNumber: string): Observable<Document[]> {
        return this.doRequest<Document[]>(`/policies/${policyNumber}/allDocuments`);
    }

    @CacheObservable(true)
    public getAllDocuments(): Observable<Document[]> {
        return this.doRequest<Document[]>(`/documents`, "GET")
    }

    @CacheObservable(true)
    public getCommunicationsByQuoteId(quoteId: string): Observable<Document[]> {
        return this.doRequest<Document[]>(`/quotes/${quoteId}/communications`);
    }

    public setDocumentsRequestedByPost(documentIds: string[], policyNumber: string): Observable<any> {
        return this.doRequest<any>(`/policies/${policyNumber}/documents/postalDocuments`, "POST", documentIds);
    }

    @CacheObservable(true)
    public getQuotes(): Observable<Quote[]> {
        return this.doRequest<Quote[]>(`/quotes/active`, "GET")
    }

    @CacheObservable(true)
    public getAllQuotes(): Observable<Quote[]> {
        return this.doRequest<Quote[]>(`/quotes/all`, "GET");
    }

    public downloadDocument(documentId: string): Observable<ArrayBuffer> {
        let _this = this;
        this.requestModifiers.push(new class implements IRequestModifier {
            modifyHttpRequest(request: AjaxRequest): Observable<AjaxRequest> {
                request.headers = request.headers || {};
                (request.headers as Record<string, any>).Accept = "application/octet-stream";
                request.responseType = "blob";
                return _this.applicationGuard.modifyHttpRequest(request);
            }
        });
        return this.doRequest(`/documents/${documentId}`);
    }

    @CacheObservable(true)
    public getPolicyHistory(policyNumber: string): Observable<Policy[]> {
        return this.doRequest<Policy[]>(`/policies/${policyNumber}/versions`, "GET")
    }

    @ClearCache("getProfile")
    @ClearCache("getUserById")
    public getPartyPropertyByPropertyKey(propertyKey: string): Observable<any> {
        return this.doRequest<PartyProperty>(`/profile/property/${propertyKey}`, "GET")
    }

    public updateMarketingPreferences(preferences: MarketingPreference): Observable<any> {
        return this.doRequest<any>(`/profile/marketingPreferences`, "POST", preferences)
    }

    public getPolicyPropertiesByPropertyKeys(policyNumber: string, propertyKeys: string[]): Observable<PolicyProperty[]> {
        return this.doRequest<PolicyProperty[]>(
            `/profile/policy/properties/${policyNumber}`,
            "POST",
            propertyKeys
        )
    }

    public isMonthlyPayerInCurrentPeriod(policyNumber: string): Observable<boolean> {
        const searchParams = new URLSearchParams();
        searchParams.append("policyNumber", policyNumber);
        return this.doRequest<boolean>(`/flow-policies/is-monthly-payer?${searchParams.toString()}`, "GET");
    }

    @ClearCache("getProfile")
    @ClearCache("getUserById")
    @ClearCache("getPolicy")
    public updateUser(user: FalconUser): Observable<any> {
        return super.getProtectedResource({
            body: user,
            method: "POST",
            headers: this.configuration.customHeaders,
            url: `${this.configuration.serviceBaseUrl}/profile`,
        }, this.configuration.customQueryStringParameters).pipe(
            map((response: AjaxResponse): any => {
                    return response.response;
                }
            ), catchError((err: AjaxError): any => {
                return throwError(() => err);
            })
        );
    }

    @ClearCache("getProfile")
    public validateNewCard(reuseCard: boolean, policyNumber?: string): Observable<NewCardForm> {
        return this.doRequest<NewCardForm>(`/policies/${policyNumber}/payment/card/validate?reuseCard=${reuseCard}`);
    }

    @ClearCache("getProfile")
    @ClearCache("getPolicyPropertyByPropertyKey")
    @ClearCache("getAnnualPolicyInfo")
    public verifyNewCard(paymentReference: string, reuseCard: boolean, policyNumber?: string): Observable<VerifyNewCardResponse> {
        return this.doRequest<VerifyNewCardResponse>(`/policies/${policyNumber}/payment/card/verify/${paymentReference}?reuseCard=${reuseCard}`)
    }

    @ClearCache("getProfile")
    public autNewCardForMissedPayment(policyNumber: string, invoiceId?: string, isChargeback?: boolean): Observable<NewCardForm> {
        const searchParams = new URLSearchParams();

        if (invoiceId) {
            searchParams.append("invoiceId", invoiceId);
        }

        if (isChargeback) {
            searchParams.append("isChargeback", isChargeback.toString());
        }

        return this.doRequest<NewCardForm>(`/policies/${policyNumber}/payment/missedPayment/autNewCard?${searchParams.toString()}`);
    }

    @ClearCache("getProfile")
    @ClearCache("getPaymentHistory")
    @ClearCache("getAnnualPolicyInfo")
    @ClearCache("getPaymentHistoryByPolicyNumber")
    @ClearCache("getPolicy")
    public completeMissedPaymentNewCard(policyNumber: string, request: MissedPayment): Observable<VerifyNewCardResponse> {
        return this.doRequest<VerifyNewCardResponse>(`/policies/${policyNumber}/payment/missedPayment/verifyNewCard`, 'POST', request)
    }

    @ClearCache("getProfile")
    @ClearCache("getPaymentHistory")
    @ClearCache("getAnnualPolicyInfo")
    @ClearCache("getPaymentHistoryByPolicyNumber")
    @ClearCache("getPolicy")
    public completeMissedPaymentWithMainPaymentCard(policyNumber: string, invoiceId?: string, isChargeback?: boolean): Observable<FalconPaymentResponse> {
        const searchParams = new URLSearchParams();

        if (invoiceId) {
            searchParams.append("invoiceId", invoiceId);
        }

        searchParams.set("isChargeback", (!!isChargeback).toString());

        return this.doRequest<FalconPaymentResponse>(`/policies/${policyNumber}/payment/missedPayment/existingCard?${searchParams.toString()}`, "POST")
    }

    @ClearCache("getProfile")
    @ClearCache("getPolicy")
    @ClearCache("getAnnualPolicyInfo")
    @ClearCache("getPolicyPropertyByPropertyKey")
    public changeCardReuseSetting(cardId: number, reuseCard: boolean, policyNumber: string): Observable<void> {
        const searchParams = new URLSearchParams();
        searchParams.append("reuseCard", reuseCard.toString());

        return this.doRequest<void>(`/policies/${policyNumber}/card/${cardId}/reuse?${searchParams.toString()}`, "POST")
    }

    public getUserById(): Observable<User> {
        return this.doRequest<User>(`/profile/party`)
    }

    public hasIdp(): Observable<boolean> {
        return this.doRequest<boolean>(`/profile/hasIdp`)
    }

    @CacheObservable(true)
    public getPaymentHistory(policyNumber: string): Observable<FerryPaymentHistoryResponse> {
        return this.doRequest<FerryPaymentHistoryResponse>(`/policies/${policyNumber}/payment/history`);
    }

    @CacheObservable(true)
    public getCurrentPeriodPaymentHistory(policyNumber: string): Observable<FerryPaymentHistoryResponse> {
        return this.doRequest<FerryPaymentHistoryResponse>(`/policies/${policyNumber}/payment/history/current-period`);
    }

    @CacheObservable(true)
    public getPastPeriodPaymentHistory(policyNumber: string): Observable<FerryPaymentHistoryResponse> {
        return this.doRequest<FerryPaymentHistoryResponse>(`/policies/${policyNumber}/payment/history/past-period`);
    }

    @CacheObservable(true)
    public getAnnualPolicyInfo(policyNumber: string): Observable<AnnualPolicyInfo> {
        return this.doRequest<AnnualPolicyInfo>(`/policies/${policyNumber}/annualPolicyInfo`);
    }

    @CacheObservable(true)
    public getCoverChanges(policyNumber: string): Observable<CoverChange[]> {
        return this.doRequest<CoverChange[]>(`/policies/${policyNumber}/coverChanges`);
    }

    @ClearCache("getAnnualPolicyInfo")
    public getSettledAmountByPaymentReference(agreementNumber: string, paymentReference: string): Observable<number> {
        return this.doRequest(`/policies/${agreementNumber}/payment/${paymentReference}/amount`, "GET");
    }

    @CacheObservable(true)
    public getInstalmentsInfo(agreementNumber: string, filterMtc: boolean): Observable<any> {
        return this.doRequest(`/policies/${agreementNumber}/payment/instalmentsInfo?filterMtc=${filterMtc}`);
    }

    @CacheObservable(true)
    public getLastPolicyVersionInstalmentsInfo(agreementNumber: string, filterMtc: boolean): Observable<any> {
        return this.doRequest(`/policies/${agreementNumber}/payment/lastPolicyVersionInstalmentsInfo?filterMtc=${filterMtc}`);
    }

    @ClearCache("getPaymentHistory")
    @ClearCache("getAnnualPolicyInfo")
    @ClearCache("getPaymentHistoryByPolicyNumber")
    public existingCardPayment(request: FalconPaymentRequest): Observable<any> {
        return this.doRequest<any>("/payment/existing-card-payment", "POST", request);
    }

    public getPaymentAmount(invoiceType: string): Observable<number> {
        return this.doRequest(`/payment/amount?invoiceType=${invoiceType}`);
    }

    public authorizeNewCard(request: FalconPaymentRequest, policyNumber: string): Observable<NewCardForm> {
        return this.doRequest<NewCardForm>(`/policies/${policyNumber}/payment/card/authorize`, "POST", request);
    }

    @ClearCache("getPaymentHistory")
    @ClearCache("getAnnualPolicyInfo")
    @ClearCache("getPaymentHistoryByPolicyNumber")
    newCardPaymentVerify(request: FalconPaymentRequest, paymentReference: string): Observable<VerifyNewCardResponse> {
        return this.doRequest<VerifyNewCardResponse>(`/payment/new-card-payment-verify/${paymentReference}`, "POST", request);
    }

    @ClearCache("getPolicy")
    @ClearCache("getPolicyPropertyByPropertyKey")
    public storePolicyAutomaticRenewalFlag(agreementNumber: string, value: boolean): Observable<any> {
        const searchParams = new URLSearchParams();
        searchParams.append("automaticRenewal", value.toString());

        return this.doRequest<any>(`/policies/${agreementNumber}/automaticRenewal?${searchParams}`, 'POST');
    }

    @ClearCache("getPolicy")
    @ClearCache("getPolicyPropertyByPropertyKey")
    public storePolicyPerformRenewalFlag(agreementNumber: string, value: boolean): Observable<any> {
        const searchParams = new URLSearchParams();
        searchParams.append("performRenewal", value.toString());

        return this.doRequest<any>(`/policies/${agreementNumber}/performRenewal?${searchParams}`, 'POST');
    }

    storePaymentDay(interactionId: string, value: number): Observable<any> {
        return this.doRequest<any>(`/profile/policies/${interactionId}/updatePaymentDay`, 'POST', value.toString());
    }

    @ClearCache("getRenewalProposal")
    storeAnnualToMonthlyPaymentDay(agreementNumber: string, value: number): Observable<any> {
        return this.doRequest<any>(`/profile/policies/${agreementNumber}/updateAnnualToMonthlyPaymentDay`, 'POST', value.toString());
    }

    @CacheObservable(true)
    public getRenewalProposal(agreementNumber: string): Observable<Renewal> {
        return this.doRequest<Renewal>(`/renewals/proposal/${agreementNumber}`, "GET");
    }

    @ClearCache("getLastPolicyVersionInstalmentsInfo")
    @ClearCache("getInstalmentsInfo")
    @ClearCache("getPaymentHistory")
    public payOffBalance(request: FalconPaymentRequest, agreementNumber: string): Observable<FalconPaymentResponse> {
        return this.doRequest<FalconPaymentResponse>(`/policies/${agreementNumber}/payment/payOffBalance/existingCard`, "POST", request);
    }

    @ClearCache("getLastPolicyVersionInstalmentsInfo")
    @ClearCache("getInstalmentsInfo")
    @ClearCache("getPaymentHistory")
    public payOffBalanceWithNewCard(request: FalconPaymentRequest, agreementNumber: string, paymentReference: string, reuseCard: boolean): Observable<VerifyNewCardResponse> {
        return this.doRequest<VerifyNewCardResponse>(`/policies/${agreementNumber}/payment/payOffBalance/newCard/${paymentReference}?reuseCard=${reuseCard}`, "POST", request);
    }

    private doRequest<T>(urlAppend: string, method?: string, body?: any): Observable<T> {
        return super.getProtectedResource({
            body,
            method: method || "GET",
            headers: this.configuration.customHeaders,
            url: `${this.configuration.serviceBaseUrl}${urlAppend}`,
        }, this.configuration.customQueryStringParameters).pipe(
            map((response: AjaxResponse): T => {
                    return response.response;
                }
            ),
            catchError(err => {
                this.notificationService.push({
                    level: "ERROR",
                    message: "We are experiencing some technical issues, please come back later"
                });
                return throwError(err);
            })
        );
    }

    @CacheObservable(true)
    public getPolicyPropertyByPropertyKey(policyNumber: string, propertyKey: string): Observable<PolicyProperty> {
        return this.doRequest<PolicyProperty>(`/profile/policyProperty/${policyNumber}/${propertyKey}`, "GET")
    }

    @ClearCache("getPolicy")
    updateDirectDebit(policyNumber: string, request: Mandate): Observable<any> {

        return super.getProtectedResource({
            body: request,
            method: "POST",
            headers: this.configuration.customHeaders,
            url: `${this.configuration.serviceBaseUrl}/policies/policy/${policyNumber}/editDirectDebit`,
        }, this.configuration.customQueryStringParameters).pipe(
            map((response: AjaxResponse): any => {
                    return response.response;
                }
            ), catchError((err: AjaxError): any => {
                return of(err);
            })
        );
    }


    @ClearCache("getPolicy")
    createDirectDebit(policyNumber: string, request: Mandate): Observable<any> {
        return super.getProtectedResource({
            body: request,
            method: "POST",
            headers: this.configuration.customHeaders,
            url: `${this.configuration.serviceBaseUrl}/policies/policy/${policyNumber}/addDirectDebit`,
        }, this.configuration.customQueryStringParameters).pipe(
            map((response: AjaxResponse): any => {
                    return response.response;
                }
            ), catchError((err: AjaxError): any => {
                return of(err);
            })
        );
    }
    getInstalmentInfoPreview(policyNumber: string, paymentDay: number): Observable<any> {
        return this.doRequest(`/profile/policy/${policyNumber}/previewPaymentDay/${paymentDay.toString()}`, 'GET');
    }

    public getAvailablePaymentDays(policyNumber: string): Observable<any> {
        return this.doRequest(`/profile/policy/${policyNumber}/availablePaymentDays`, 'GET');
    }

    public getInteractionByStatus(status: string[]): Observable<any> {
        return this.doRequest<any>(`/interaction?status=${status.join(",")}`, 'GET')
    }

    public acceptOrCancelMigratedRenewal(interactionId: string, accept: boolean): Observable<any> {
        return this.doRequest<any>(`/interaction/${interactionId}/migratedRenewalDecision?acceptRenewal=${accept}`, 'POST');
    }

    public cancelQuote(interactionId: string): Observable<any> {
        return this.doRequest<any>(`/interaction/cancelQuote/${interactionId}`, 'POST');
    }

    public setReuseCardByInteraction(interactionId: string, value: boolean) : Observable<boolean> {
        const searchParams = new URLSearchParams();
        searchParams.append("reuseCard", value.toString());

        return this.doRequest<any>(`/policies/interaction/${interactionId}/reuse?${searchParams.toString()}`, 'POST');
    }

    public getPolicyPropertyMainAnnualPaymentCard(policyNumber: string): Observable<number> {
        return this.doRequest<number>(`/policies/${policyNumber}/mainAnnualPaymentCard`, "GET");
    }
}
